import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SERVICES, MARKETS, ISSUES, IMG, UI } from "../data/content";
import { Reveal, SectionHead, CapCard, CTAButton, ArrowLink, ArticleCard, ContactModule, usePageMeta, useLatestArticles } from "../components/kit";

const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <header className="hero-band" style={{ minHeight: "560px" }} data-testid="home-hero">
      <motion.img src={IMG.athens} alt="Athens business district at dusk" className="hero-img"
        initial={reduce ? false : { scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: "easeOut" }} />
      <div className="hero-scrim" />
      <div className="k-container hero-content w-full py-20">
        <motion.div initial={reduce ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
          <p className="eyebrow mb-4">{UI.home.eyebrow}</p>
          <h1 className="k-h1 max-w-3xl" data-testid="hero-title">{UI.home.title}</h1>
          <p className="standfirst max-w-2xl mt-6">{UI.home.standfirst}</p>
          <div className="mt-9">
            <CTAButton to="/services" variant="inverse" testId="hero-explore-btn">{UI.home.cta}</CTAButton>
          </div>
        </motion.div>
      </div>
    </header>
  );
};

export default function Home() {
  usePageMeta("KairosHK | EU funding & cross-border advisory · Greece · Cyprus · EU", UI.home.standfirst);
  const articles = useLatestArticles(3);
  const featured = articles[0];
  const rest = articles.slice(1, 3);

  return (
    <div data-testid="home-page">
      <Hero />

      <section className="k-section !pb-14" data-testid="home-preamble">
        <div className="k-container">
          <Reveal>
            <div className="max-w-4xl border-l-2 border-[var(--accent)] pl-8 md:pl-12 py-2">
              <p className="text-xl md:text-2xl leading-relaxed text-ink mb-6" style={{ textWrap: "balance" }}>{UI.home.pre1}</p>
              <p className="text-lg leading-relaxed text-ink-muted">{UI.home.pre2}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="k-section bg-paper-alt band-texture" data-testid="home-services-section">
        <div className="k-container">
          <SectionHead eyebrow={UI.home.servicesEyebrow} title={UI.home.servicesTitle} />
          <div className="grid md:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <CapCard key={s.slug} to={`/services/${s.slug}`} title={s.name} body={s.proposition} i={i} testId={`service-card-${s.slug}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="k-section" data-testid="home-markets-section">
        <div className="k-container">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow={UI.nav.markets} title={UI.home.marketsTitle} lead={UI.home.marketsLead} />
            <div className="mb-10"><ArrowLink to="/markets" testId="home-all-markets-link">{UI.home.allMarkets}</ArrowLink></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {MARKETS.map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.06} className="h-full">
                <Link to={`/markets/${m.slug}`} className="cap-card !p-0 overflow-hidden group" data-testid={`market-panel-${m.slug}`}>
                  <div className="relative h-40 overflow-hidden">
                    <img src={IMG[m.image]} alt={m.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-base font-semibold cap-title mb-3">{m.name}</h3>
                    <p className="text-ink-muted text-sm leading-relaxed mb-5 flex-1">{m.proposition}</p>
                    <span className="arrow-link text-sm">{UI.common.readMore} <ArrowRight size={14} className="arr" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="k-section" style={{ background: "var(--brand-dark)" }} data-testid="home-funding-section">
        <div className="k-container grid lg:grid-cols-2 gap-12 items-stretch">
          <Reveal className="h-full">
            <img src={IMG.piraeus} alt="Container port of Piraeus" className="w-full h-full min-h-[320px] max-h-[640px] object-cover" data-testid="home-funding-image" />
          </Reveal>
          <div className="flex flex-col justify-center">
            <p className="eyebrow mb-3" style={{ color: "#E5A873" }}>{UI.home.fundingEyebrow}</p>
            <h2 className="k-h2 mb-4 text-white">{UI.home.fundingTitle}</h2>
            <p className="mb-8 leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>{UI.home.fundingLead}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {UI.home.fundingTools.map(([label, desc, to], i) => (
                <Link key={to} to={to} className={`border p-5 transition-colors hover:border-[var(--accent)] ${i === UI.home.fundingTools.length - 1 && UI.home.fundingTools.length % 2 === 1 ? "sm:col-span-2" : ""}`} style={{ borderColor: "rgba(255,255,255,0.2)" }} data-testid={`home-tool-${to.split("/").pop() || "funding"}`}>
                  <p className="font-semibold text-white mb-1 flex items-center gap-2">{label} <ArrowRight size={14} style={{ color: "var(--accent)" }} /></p>
                  <p className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="k-section" data-testid="home-issues-section">
        <div className="k-container">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow={UI.nav.issues} title={UI.home.issuesTitle} />
            <div className="mb-10"><ArrowLink to="/issues" testId="home-all-issues-link">{UI.home.allIssues}</ArrowLink></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {ISSUES.map((u, i) => (
              <Reveal key={u.slug} delay={i * 0.06}>
                <Link to={`/issues/${u.slug}`} className="cap-card !p-6" data-testid={`issue-card-${u.slug}`}>
                  <h3 className="text-base font-semibold cap-title mb-3">{u.name}</h3>
                  <p className="text-ink-muted text-sm leading-relaxed flex-1">{u.card}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="k-section bg-paper-alt" data-testid="home-insights-section">
        <div className="k-container">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow={UI.home.insightsEyebrow} title={UI.home.insightsTitle} />
            <div className="mb-10"><ArrowLink to="/insights" testId="home-all-insights-link">{UI.home.allInsights}</ArrowLink></div>
          </div>
          {featured && (
            <Reveal className="mb-2">
              <div className="border border-line bg-paper p-8 lg:p-10 hover:border-[var(--accent)] transition-colors">
                <ArticleCard a={featured} featured />
              </div>
            </Reveal>
          )}
          <Reveal>
            <div className="border-b border-line">
              {rest.map((a) => <ArticleCard key={a.slug} a={a} />)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="k-section" data-testid="home-cta-section">
        <div className="k-container grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHead eyebrow={UI.home.ctaEyebrow} title={UI.home.ctaTitle} lead={UI.home.ctaLead} />
            <ArrowLink to="/about-us" testId="home-about-link">{UI.home.aboutLink}</ArrowLink>
          </div>
          <ContactModule />
        </div>
      </section>
    </div>
  );
}
